import React, { useCallback, useEffect, useState } from 'react'
import { Field } from 'redux-form'
import renderSelect from 'components/FormControls/select'
import GridSpinner from 'components/UI/GridSpinner'
import { useTranslation } from 'react-i18next'
import { Grid, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import uniqBy from 'lodash/uniqBy'
import SERVICES_API_V1 from 'api/services/v1'
import formatMoney from 'utils/formatMoney'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'
import { isRequiredField } from 'utils/isRequiredField'
import { FacebookHighRiskSchema } from 'modules/Forms/Account/Create/FacebookHighRisk'

const ProviderFields = ({ autofill, form, networkId, onProviderChange, networkType }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [providersFullList, setProvidersFullList] = useState([])
  const [providers, setProviders] = useState([])
  const [geo, setGeo] = useState([])
  const [currency, setCurrency] = useState([])
  const { i18n, values = {} } = useSelector((state) => ({
    i18n: state['app'].i18n,
    values: state.form[form].values,
  }))

  const initSelectors = useCallback(
    (providers) => {
      setCurrency(
        uniqBy(providers, 'currency').map((provider) => ({
          label: provider.currency,
          value: provider.currency,
        })),
      )

      setGeo(
        uniqBy(providers, 'geo').map((provider) => ({
          label: provider.geo,
          value: provider.geo,
        })),
      )
    },
    [setCurrency, setGeo],
  )

  useEffect(async () => {
    if (!networkId) {
      return
    }

    const fetchedData = await SERVICES_API_V1.getNetworkSettings({
      networkId,
      params: { lang: i18n },
    }).then(({ response: { data } }) => {
      return data?.providers.map((provider) => ({
        ...provider,
        value: provider.id,
        label: provider.name,
      }))
    })

    setProvidersFullList((prev) => [...prev, ...fetchedData])
    setProviders((prev) => [...prev, ...fetchedData])
    initSelectors(fetchedData)

    fetchedData.length === 1 && autofill('provider_id', fetchedData[0].id)

    setLoading(false)
  }, [networkId])

  useEffect(() => {
    if (values?.provider_id) {
      autofill('geo', providers.find((provider) => provider.id === values.provider_id)?.geo)
      autofill(
        'currency',
        providers.find((provider) => provider.id === values.provider_id)?.currency,
      )
    }
  }, [values?.provider_id])

  useEffect(() => {
    if (values?.geo) {
      setCurrency(
        uniqBy(
          providersFullList.filter((provider) => provider.geo === values?.geo),
          'currency',
        ).map((provider) => ({
          value: provider.currency,
          label: provider.currency,
        })),
      )
    } else {
      initSelectors(providersFullList)
    }

    setProviders(() =>
      providersFullList.filter(
        (provider) =>
          (values?.geo ? provider.geo === values?.geo : true) &&
          (values?.currency ? provider.currency === values?.currency : true),
      ),
    )
  }, [values?.geo, values?.currency])

  useEffect(() => {
    onProviderChange(providers.find((provider) => provider.id === +values?.provider_id))
  }, [values?.provider_id, providers])

  return (
    <>
      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          label={t('services::forms.shared_fields.geo.label')}
          name="geo"
          required={isRequiredField(FacebookHighRiskSchema(networkType), 'geo')}
          component={renderSelect}
          onChangeOption={() => {
            autofill('provider_id', undefined)
          }}
          fullWidth
          t={t}
          normalize={(value) => value ?? ''}
          emptyOptionLabel={t('common::select_empty_option', {
            context: 'not_selected',
          })}
          clearOptionLabel={t('common::select_clear_option')}
          options={geo}
          readOnly={loading}
          ControlProps={{
            startAdornment: loading && <GridSpinner width={24} />,
          }}
        />
      </div>
      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          label={t('services::forms.shared_fields.currency.label')}
          name="currency"
          required={isRequiredField(FacebookHighRiskSchema(networkType), 'currency')}
          component={renderSelect}
          onChangeOption={() => {
            autofill('provider_id', undefined)
          }}
          fullWidth
          t={t}
          normalize={(value) => value ?? ''}
          emptyOptionLabel={t('common::select_empty_option', {
            context: 'not_selected',
          })}
          clearOptionLabel={t('common::select_clear_option')}
          options={currency}
          readOnly={loading}
          ControlProps={{
            startAdornment: loading && <GridSpinner width={24} />,
          }}
        />
      </div>
      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          label={t('services::forms.shared_fields.provider_id.label')}
          name="provider_id"
          required={isRequiredField(FacebookHighRiskSchema(networkType), 'provider_id')}
          component={renderSelect}
          fullWidth
          optionLabelTemplate={(providerRow) => (
            <Grid
              direction={'column'}
              gap={4}
            >
              <Typography variant={'Body-15'}>{providerRow.label}</Typography>
              <Typography
                variant={'Body-10'}
                color="Label-Secondary"
                dangerouslySetInnerHTML={{
                  __html: t('services::forms.shared_fields.provider_id.option_label', {
                    min_amount: formatMoney(providerRow.min_amount, providerRow.currency),
                    setup_fee: formatMoney(providerRow.setup_fee, providerRow.currency),
                  }),
                }}
              />
            </Grid>
          )}
          t={t}
          normalize={(value) => value ?? ''}
          emptyOptionLabel={t('common::select_empty_option', {
            context: 'not_selected',
          })}
          clearOptionLabel={t('common::select_clear_option')}
          options={providers}
          readOnly={loading}
          ControlProps={{
            startAdornment: loading && <GridSpinner width={24} />,
          }}
        />
      </div>
    </>
  )
}

export default ProviderFields

ProviderFields.propTypes = {
  autofill: PropTypes.func,
  form: PropTypes.string,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  providersFullList: PropTypes.array,
  values: PropTypes.object,
  loading: PropTypes.bool,
  onProviderChange: PropTypes.func,
}
